/** Additional attempts after the first failure (total attempts = maxRetries + 1). */
export const FETCH_MAX_RETRIES = 2;
export const FETCH_RETRY_INITIAL_DELAY_MS = 300;

const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

function sleep(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function getAbortSignal(input, init) {
    if (init?.signal) {
        return init.signal;
    }
    if (typeof Request !== 'undefined' && input instanceof Request) {
        return input.signal;
    }
    return null;
}

export function isAbortError(error) {
    if (!error) {
        return false;
    }
    if (error.name === 'AbortError') {
        return true;
    }
    // DOMException in browsers; some polyfills use code 20.
    return error.code === 20 || error.code === 'ABORT_ERR';
}

/**
 * Transient transport failures (Chrome ERR_NETWORK_CHANGED → TypeError: Failed to fetch).
 */
export function isRetryableFetchError(error) {
    if (!error || isAbortError(error)) {
        return false;
    }
    if (error.name === 'TypeError') {
        return true;
    }
    const message = String(error.message || error).toLowerCase();
    return (
        message.includes('failed to fetch')
        || message.includes('networkerror')
        || message.includes('network changed')
        || message.includes('load failed')
        || message.includes('fetch failed')
    );
}

export function isRetryableHttpStatus(status) {
    return RETRYABLE_STATUS.has(Number(status));
}

function backoffDelayMs(attempt, initialDelayMs) {
    const base = initialDelayMs * (2 ** attempt);
    const jitter = Math.floor(Math.random() * Math.min(100, base * 0.25));
    return base + jitter;
}

function cloneFetchInput(input) {
    if (typeof Request !== 'undefined' && input instanceof Request) {
        return input.clone();
    }
    return input;
}

function canRetryRequest(input, init) {
    const signal = getAbortSignal(input, init);
    if (signal?.aborted) {
        return false;
    }
    // ReadableStream bodies cannot be replayed unless the Request is cloneable.
    if (init?.body && typeof ReadableStream !== 'undefined' && init.body instanceof ReadableStream) {
        return false;
    }
    return true;
}

async function discardResponseBody(response) {
    try {
        if (response && typeof response.arrayBuffer === 'function') {
            await response.arrayBuffer();
        }
    } catch {
        /* ignore */
    }
}

/**
 * Retry a native fetch invocation on transient network / gateway failures.
 *
 * @param {typeof fetch} nativeFetch
 * @param {Parameters<typeof fetch>} args
 * @param {{ maxRetries?: number, initialDelayMs?: number, sleepFn?: (ms: number) => Promise<void> }} [options]
 * @returns {Promise<Response>}
 */
export async function fetchWithNetworkRetries(
    nativeFetch,
    args,
    {
        maxRetries = FETCH_MAX_RETRIES,
        initialDelayMs = FETCH_RETRY_INITIAL_DELAY_MS,
        sleepFn = sleep,
    } = {},
) {
    const input = args[0];
    const init = args[1];
    const retriesEnabled = canRetryRequest(input, init);
    const attempts = retriesEnabled ? maxRetries + 1 : 1;

    let lastError;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
        const signal = getAbortSignal(input, init);
        if (signal?.aborted) {
            throw signal.reason || new DOMException('The operation was aborted.', 'AbortError');
        }

        let attemptInput;
        try {
            attemptInput = cloneFetchInput(input);
        } catch (cloneErr) {
            if (attempt === 0) {
                attemptInput = input;
            } else {
                throw lastError || cloneErr;
            }
        }

        try {
            const response = await nativeFetch(attemptInput, init);
            const shouldRetryStatus = retriesEnabled
                && attempt < attempts - 1
                && isRetryableHttpStatus(response.status);

            if (!shouldRetryStatus) {
                return response;
            }

            await discardResponseBody(response);
            await sleepFn(backoffDelayMs(attempt, initialDelayMs));
        } catch (error) {
            lastError = error;
            if (!retriesEnabled || !isRetryableFetchError(error) || attempt >= attempts - 1) {
                throw error;
            }
            await sleepFn(backoffDelayMs(attempt, initialDelayMs));
        }
    }

    throw lastError || new Error('fetchWithNetworkRetries: exhausted retries');
}
